import type { ClientResponse } from '@shopify/storefront-api-client';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';

const {
  VITE_SHOPIFY_STORE_DOMAIN: storeDomain,
  VITE_SHOPIFY_STOREFRONT_API_VERSION: apiVersion,
  VITE_SHOPIFY_PUBLIC_STOREFRONT_TOKEN: publicAccessToken
} = import.meta.env;

if (!storeDomain || !apiVersion || !publicAccessToken) {
  throw new Error(
    'Missing Shopify Storefront API settings: set VITE_SHOPIFY_STORE_DOMAIN, VITE_SHOPIFY_STOREFRONT_API_VERSION ' +
      'and VITE_SHOPIFY_PUBLIC_STOREFRONT_TOKEN (see .env.example).'
  );
}

/**
 * Shopify Storefront API client, shared by the build (static server functions, e.g. products) and the browser
 * (e.g. cart, favourites). The site is statically generated, so the browser calls the Storefront API directly
 * with the public access token, which is meant to be public.
 *
 * Queries and their result types: storefront-api-client-queries/queries.ts and types/ (pnpm graphql-codegen).
 */
export const storefrontClient = createStorefrontApiClient({
  storeDomain,
  apiVersion, // https://shopify.dev/changelog?filter=api&api_type=storefront-graphql
  publicAccessToken,
  // retries a request after 1 second on network errors and 429 (throttled) / 503 responses, e.g. while prerendering
  retries: 2
});

/** Returns the data of a Storefront API response, throws on errors. */
export function dataOrThrow<TData>({ data, errors }: ClientResponse<TData>) {
  if (errors) {
    throw new Error(errors.message ?? 'Shopify Storefront API error', {
      cause: errors
    });
  }

  if (!data) {
    throw new Error('Shopify Storefront API returned no data.');
  }

  return data;
}
